import { useEffect } from "react";

/**
 * Add data-cancel-click-away={["cancelKey1", "cancelkey2"] || "cancelKey1,cancelKey2"} to any element that should not trigger the useOnClickOutside handler.
 * @param {Element} elem
 */
const parentHasNoClickAway = (elem, cancelKey) => {
  if (!elem || !cancelKey) return false;

  if (!elem.tagName || elem.tagName.toLowerCase() === "body") return false;

  if (typeof elem.dataset.cancelClickAway === "string") {
    let cancelKeys = elem.dataset.cancelClickAway.split(",");
    if (cancelKeys.includes(cancelKey)) return true;
  }

  return parentHasNoClickAway(elem.parentNode, cancelKey);
};

/**
 *
 * @param {import("react").Ref} ref
 * @param {function} handler - MUST be wrapped in a callback.
 * @param {string} cancelKey Add data-cancel-click-away={["cancelKey1", "cancelkey2"] || "cancelKey1,cancelKey2"}  to any element that should not trigger the useOnClickOutside handler.
 */
const useOnClickOutside = (ref, handler, cancelKey) => {
  useEffect(() => {
    const pointerListener = (event) => {
      if (
        !ref.current ||
        ref.current.contains(event.target) ||
        parentHasNoClickAway(event.target, cancelKey)
      ) {
        return;
      }
      handler(event);
    };

    const keyListener = (event) => {
      if (event.key === "Escape" && ref.current?.contains(event.target)) {
        handler(event);
      } else if (event.key === "Tab") {
        const active = document.activeElement;
        if (ref.current && !ref.current.contains(active)) {
          handler(event);
        }
      }
    };

    document.addEventListener("mousedown", pointerListener);
    document.addEventListener("touchstart", pointerListener);
    document.addEventListener("focusin", pointerListener);
    document.addEventListener("keydown", keyListener);

    return () => {
      document.removeEventListener("mousedown", pointerListener);
      document.removeEventListener("touchstart", pointerListener);
      document.removeEventListener("focusin", pointerListener);
      document.removeEventListener("keydown", keyListener);
    };
  }, [ref, handler, cancelKey]);
};

export { parentHasNoClickAway, useOnClickOutside };
export default useOnClickOutside;
